/* THE LEADERSHIP IMPRINT – assessment flow controller.
   Black Sheep Leadership Group. Drives the whole take-it experience per the
   Instructions doc: intro screens, 40 primary items (Blocks A-E) with transitions,
   the Mirror (12), a Fleet estimate, kudos + completion, then renders the report.

   window.TLI.app.mount(rootEl, opts)  opts: { mode:'self'|'org', withhold:false,
     onComplete(result), images }  (images passed through to the report renderer).

   Copy rules honoured: no em dashes, second person, never "test", no "score",
   progress as "X of N" not a bar, options randomised, no partial save/resume.
*/
(function (root) {
  'use strict';
  function el(tag, attrs, kids) {
    var e = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'text') e.textContent = attrs[k];
      else if (k === 'html') e.innerHTML = attrs[k];
      else e.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) e.appendChild(c); });
    return e;
  }
  function shuffle(a) { a = a.slice(); for (var i = a.length - 1; i > 0; i--) { var j = Math.floor(Math.random() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t; } return a; }

  var FREQ = ['Almost never', 'Rarely', 'Sometimes', 'Often', 'Almost always'];

  // ---- intro + transition + completion copy (Instructions doc) --------
  var INTRO = [
    { eyebrow: 'The Leadership Imprint', h: 'Before you start',
      body: [
        'This takes about fifteen minutes, and it comes in two parts. First, how you lead. Then a shorter set answered as you think your team would answer about you.',
        '<b>Do this in one sitting.</b> There is no way to save halfway through, so if you close the tab you start again from the beginning.',
        'Find somewhere quiet and give it your full attention. Not in a meeting, not between two other things. Several questions will make you stop and think, and the thinking is where the accuracy comes from.',
        '<b>Answer as you actually are</b>, not as the leader you are working on becoming. This looks at what you do on an ordinary Tuesday, including the parts you are not proud of.',
        'There is no right answer in here and no style that comes out ahead. You are not being ranked. You are being described.'
      ], btn: 'Continue' },
    { eyebrow: 'The Leadership Imprint', h: 'How to answer',
      body: [
        '<b>Go with your first instinct.</b> Read the question, notice what comes to you, and answer that. If you catch yourself building a case, go back to the instinct.',
        '<b>You will have to pick one.</b> Most questions give you two statements and ask which is more true of you. There is no middle option, on purpose. Both will often be true. Pick the one that is more true, more often.',
        '"It depends" is not a problem. Answer for what you do most of the time, when nothing is forcing you either way. That default is what this measures. Pressure comes later, separately.',
        '<b>Some questions will be uncomfortable.</b> Part of this asks how you behave when things are going badly. The honest answer there is worth more than the whole of the rest.'
      ], btn: 'Continue' },
    { eyebrow: 'The Leadership Imprint', h: 'Who sees this',
      body: [
        '<b>You do.</b> Your answers and your report are yours. Nobody else receives a copy, and nobody is notified that you took it.',
        'We keep your responses so we can improve the instrument over time. That is looked at in aggregate, alongside everybody else’s, and never attributed to you individually.',
        'We do not sell your data, we do not share it with employers, and we do not use it for anything other than making the assessment better.'
      ], btn: 'Continue' },
    { eyebrow: 'The Leadership Imprint', h: 'Ready',
      body: [
        'Fifteen minutes. Somewhere quiet. First instinct.',
        '<b>Answer as the leader you actually are, not the one you are working on becoming.</b> That leader is the one who gets something useful out of this.'
      ], btn: 'Begin the assessment' }
  ];

  var TRANSITIONS = {
    A: { h: 'First, how you move', body: ['Two statements each time. Pick the one more true of you.'] },
    B: { h: 'Now, what you protect', body: ['Same format. Both will often be true. Pick the stronger lean.'] },
    C: { h: 'Now the harder part', body: ['These are situations rather than statements, and they ask what you do when things are going badly.', '<b>Answer honestly rather than well.</b> This section is where the useful part of your report comes from.'] },
    D: { h: 'Nearly done', body: ['A few questions about how consistent you are. Rate how often each one is true.'] },
    E: { h: 'Last three', body: ['These are about disagreement. Your answers here are held back from your report and released separately, because they land better with somebody to talk them through.'] }
  };

  root.TLI = root.TLI || {};
  root.TLI.app = {
    mount: function (rootEl, opts) {
      opts = opts || {};
      var E = root.TLI.engine, report = root.TLI.report;
      var state = { intake: { firstName: '', lastName: '', title: '', company: '' }, A: {}, B: {}, C: {}, D: {}, E: {}, M: {}, fleet: { rocket: '', jet: '', tractor: '', bus: '' } };
      // Corporate cohort mode: taker reserves a seat, never sees their own report.
      var cohortId = opts.cohort || null, PROXY = opts.proxy || '/apps/p2p/li';
      var cohortCode = opts.code || '';
      state.cohortSid = null; state.startedAt = 0;

      var ENCOURAGE = {
        honesty: { icon: '✓', eyebrow: 'Part one · You', h: 'You are doing well', body: ['One thing to hold onto as you go. Answer for the leader you are on an ordinary day, not the one you are working on becoming. That is the one this is built for.'], btn: 'Keep going' },
        hardpart: { icon: '◆', eyebrow: 'Part one · You', h: 'That was the harder stretch', body: ['The honest answers in that section are worth more than the rest put together. Most leadership gets judged on hard days, so the way you handle them is the part worth knowing.'], btn: 'Keep going' }
      };

      // Build the ordered step list. Each step carries a phase for the stepper.
      var steps = [];
      function push(s) { steps.push(s); }
      if (opts.short) {
        // Short "try it" lead magnet: 10 quick either-or questions -> feeder result -> two paths.
        push({ t: 'screen', s: { eyebrow: 'A 2-minute taste', h: 'Ten quick questions', body: [
          'A short taste of the Leadership Imprint. Ten quick either-or questions, about two minutes, and no sign-up.',
          'Answer for the leader you are on an ordinary day. At the end you will see which of the four styles you lead like.'
        ], btn: 'Start' }, phase: 'you' });
        var sq = 0;
        E.BLOCK_A.slice(0, 5).forEach(function (q) { sq++; push({ t: 'q', key: 'A', q: q, fmt: 'forced', prog: 'Question ' + sq + ' of 10', phase: 'you' }); });
        E.BLOCK_B.slice(0, 5).forEach(function (q) { sq++; push({ t: 'q', key: 'B', q: q, fmt: 'forced', prog: 'Question ' + sq + ' of 10', phase: 'you' }); });
        push({ t: 'shortresult', phase: 'report' });
      } else {
      push({ t: 'intake', phase: 'you' });
      // Cohort takers get an accurate "Who sees this" — their organizer receives
      // completion + style (and their sealed workbook), never their answers.
      var introList = INTRO.map(function (s) {
        if (cohortId && s.h === 'Who sees this') return { eyebrow: s.eyebrow, h: 'Who sees this', body: [
          'Your company set this up, so your organizer will see that you finished and which style you are. That’s how the group part works. They need it to set up the session.',
          'They receive your workbook sealed, to hand to you. They don’t see your answers.',
          'You’ll get your own full result.'
        ], btn: 'Continue' };
        return s;
      });
      introList.forEach(function (s) { push({ t: 'screen', s: s, phase: 'you' }); });
      push({ t: 'warmup', phase: 'you' });
      var PRIMARY = [
        { key: 'A', block: E.BLOCK_A, fmt: 'forced' }, { key: 'B', block: E.BLOCK_B, fmt: 'forced' },
        { key: 'C', block: E.BLOCK_C, fmt: 'scenario' }, { key: 'D', block: E.BLOCK_D, fmt: 'freq' },
        { key: 'E', block: E.BLOCK_E, fmt: 'forced' }
      ];
      var pnum = 0;
      PRIMARY.forEach(function (blk) {
        push({ t: 'screen', s: { eyebrow: 'Part one · You', h: TRANSITIONS[blk.key].h, body: TRANSITIONS[blk.key].body, btn: 'Continue' }, phase: 'you' });
        blk.block.forEach(function (q) { pnum++; push({ t: 'q', key: blk.key, q: q, fmt: blk.fmt, prog: 'Question ' + pnum + ' of 40', phase: 'you' }); });
        if (blk.key === 'A') push({ t: 'screen', s: ENCOURAGE.honesty, phase: 'you' });
        if (blk.key === 'C') push({ t: 'screen', s: ENCOURAGE.hardpart, phase: 'you' });
      });
      push({ t: 'screen', s: {
        eyebrow: 'Part two · Your team', h: 'Well done', body: [
          'That is how you lead. Now the shorter, harder part.',
          '<b>Twelve more, answered as your team would answer them about you.</b> Not how you see yourself. How you think the people who work for you would describe you if nobody was watching and there were no consequences for saying it.',
          '<b>Be honest rather than generous.</b> If you answer these the way you would like to be seen, you get two identical results and learn nothing. The gap between them is the entire point.'
        ], btn: 'Continue' }, phase: 'team' });
      var mnum = 0;
      E.BLOCK_M.forEach(function (q) { mnum++; push({ t: 'q', key: 'M', q: q, fmt: 'forced', prog: 'Question ' + mnum + ' of 12', phase: 'team' }); });
      push({ t: 'screen', s: { eyebrow: 'Part two · Your team', h: 'Nicely done', body: ['One last thing, and it is quick. A rough read of your team helps the report show what the group is missing.'], btn: 'Continue' }, phase: 'team' });
      push({ t: 'fleet', phase: 'team' });
      push({ t: 'screen', s: { icon: '★', eyebrow: 'That is it', h: 'That is it', body: cohortId ? [
        'Your work paid off. That is every question answered.',
        'Your answers go straight to your organizer, who will share the results with you as a group. You will not see your own result here, and that is by design.'
      ] : [
        'Your work paid off. Your report is being put together now.',
        '<b>A suggestion before you read it.</b> Give it the same care you gave the questions. The first two pages tell you your style. The rest tells you what to do about it, and that is the part worth having.'
      ], btn: cohortId ? 'Submit my answers' : 'See my report' }, phase: 'report' });
      push({ t: 'report', phase: 'report' });
      }

      var i = 0;
      function go(n) { i = Math.max(0, Math.min(steps.length - 1, n)); render(); }

      function progressBar(label) {
        return el('div', { class: 'tliapp-prog' }, [el('span', { text: label })]);
      }

      // Corporate takers never see their own report, so the last step is "Done".
      var PHASES = [{ k: 'you', n: 'You' }, { k: 'team', n: 'Your Team' }, { k: 'report', n: cohortId ? 'Done' : 'Your Report' }];
      function stepper(current) {
        var curIdx = PHASES.map(function (p) { return p.k; }).indexOf(current);
        var box = el('div', { class: 'tliapp-stepper' });
        PHASES.forEach(function (p, n) {
          if (n > 0) box.appendChild(el('span', { class: 'tliapp-stepline' + (n <= curIdx ? ' done' : '') }));
          var cls = 'tliapp-step' + (n === curIdx ? ' on' : n < curIdx ? ' done' : '');
          box.appendChild(el('div', { class: cls }, [
            el('span', { class: 'tliapp-stepnum', text: n < curIdx ? '✓' : String(n + 1) }),
            el('span', { class: 'tliapp-steplbl', text: p.n })
          ]));
        });
        return box;
      }

      function render() {
        var step = steps[i];
        rootEl.innerHTML = '';
        var wrap = el('div', { class: 'tliapp' });
        rootEl.appendChild(wrap);
        window.scrollTo(0, 0);
        if (step.phase && step.t !== 'report' && step.t !== 'shortresult' && !opts.short) wrap.appendChild(stepper(step.phase));
        if (step.t === 'intake') return cohortId ? renderCohortIntake(wrap) : renderIntake(wrap);
        if (step.t === 'warmup') return renderWarmup(wrap);
        if (step.t === 'screen') return renderScreen(wrap, step.s);
        if (step.t === 'q') return renderQ(wrap, step);
        if (step.t === 'fleet') return renderFleet(wrap);
        if (step.t === 'shortresult') return renderShort(wrap);
        if (step.t === 'report') return renderReport(wrap);
      }

      function renderWarmup(wrap) {
        var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
          el('div', { class: 'tliapp-eyebrow', text: 'Before you start · A quick look' }),
          el('h2', { class: 'tliapp-h', text: 'Here is what most of it looks like' }),
          el('p', { html: 'Most questions give you two statements and ask which is <b>more</b> true of you. There is no middle option. Both will often be true, so pick the one that is more true, more often. Try it here.' }),
          el('div', { class: 'tliapp-qtext', text: 'I am more inclined to…', style: 'margin-top:8px' })
        ]);
        var demo = el('div', { class: 'tliapp-opts tliapp-opts--two' });
        [['Decide, then adjust as I go', 'Understand it fully, then decide']].forEach(function (pair) {
          pair.forEach(function (txt) {
            var b = el('button', { class: 'tliapp-opt', type: 'button', text: txt });
            b.addEventListener('click', function () {
              demo.querySelectorAll('.tliapp-opt').forEach(function (o) { o.classList.remove('picked'); });
              b.classList.add('picked');
              cont.disabled = false; cont.textContent = 'Got it, let us begin';
            });
            demo.appendChild(b);
          });
        });
        card.appendChild(demo);
        card.appendChild(el('p', { class: 'tliapp-fine', html: 'Neither one is better. Nobody sees this practice answer. When you are ready, we will begin for real.' }));
        var cont = el('button', { class: 'tliapp-btn', type: 'button', text: 'Pick one above to continue', disabled: 'disabled' });
        cont.addEventListener('click', function () { if (!cont.disabled) go(i + 1); });
        card.appendChild(cont);
        wrap.appendChild(card);
      }

      // Corporate intake: server-normalized name, live duplicate disambiguation,
      // a confirm-name step, and a cohort-correct disclosure with a research opt-out.
      function renderCohortIntake(wrap) {
        function esc(s) { return String(s == null ? '' : s).replace(/[&<>]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]; }); }
        if (!state._ci) state._ci = { first: state.intake.firstName || '', last: state.intake.lastName || '', title: state.intake.title || '', code: cohortCode || '', suffix: '', confirmDup: false, optOut: false, display: '', busy: false, note: '', stage: 'form' };
        var ci = state._ci;

        function submit(isCheck) {
          ci.busy = true; if (!isCheck) ci.note = 'One moment…'; paint();
          var nm = (ci.first + ' ' + ci.last).trim();
          fetch(PROXY, { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'same-origin',
            body: JSON.stringify({ action: 'start', c: cohortId, name: nm, code: ci.code, suffix: ci.suffix, confirmDup: ci.confirmDup, research: !ci.optOut, check: !!isCheck }) })
            .then(function (r) { return r.json(); })
            .then(function (d) {
              ci.busy = false;
              if (d && d.error === 'badcode') { ci.stage = 'form'; ci.note = d.message || 'That company access code is not right. Check with your organizer.'; paint(); return; }
              if (d && d.error === 'full') { ci.note = d.message || 'Every seat for this assessment has been used.'; paint(); return; }
              if (d && d.duplicate) { ci.display = d.display; ci.company = d.company || ci.company; ci.dupAt = d.startedAt || 0; ci.dupDone = !!d.alreadyDone; ci.stage = 'dup'; ci.note = ''; paint(); return; }
              if (isCheck && d && d.ok) { ci.display = d.display; ci.company = d.company || ci.company; ci.stage = 'confirm'; ci.note = ''; paint(); return; }
              if (d && d.ok && d.sessionId) { state.cohortSid = d.sessionId; if (d.company) state.intake.company = d.company; state.intake.firstName = ci.first; state.intake.lastName = ci.last; state.startedAt = Date.now(); go(i + 1); return; }
              ci.note = 'Something went wrong. Please try again.'; paint();
            })
            .catch(function () { ci.busy = false; ci.note = 'Could not reach the server. Please try again.'; paint(); });
        }

        function noteEl() { return el('div', { class: 'tliapp-note', text: ci.note || '' }); }
        function ago(ts) {
          if (!ts) return '';
          var m = Math.round((Date.now() - ts) / 60000);
          if (m < 1) return 'just now';
          if (m === 1) return 'about a minute ago';
          if (m < 60) return 'about ' + m + ' minutes ago';
          var h = Math.round(m / 60);
          return h === 1 ? 'about an hour ago' : 'about ' + h + ' hours ago';
        }

        function paintForm() {
          var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
            el('div', { class: 'tliapp-eyebrow', text: 'The Leadership Imprint' }),
            el('h2', { class: 'tliapp-h', text: 'Let us start with you' }),
            el('p', { text: 'Your organizer set this up for your team. Add your name and the access code they gave you.' })
          ]);
          function field(label, key, ph) {
            var inp = el('input', { class: 'tliapp-input', type: 'text', placeholder: ph || '', value: ci[key] || '' });
            inp.addEventListener('input', function () { ci[key] = inp.value; });
            return el('label', { class: 'tliapp-field' }, [el('span', { text: label }), inp]);
          }
          card.appendChild(el('div', { class: 'tliapp-row' }, [field('First name', 'first'), field('Last name', 'last')]));
          card.appendChild(field('Position or title', 'title', 'e.g. Operations Manager'));
          var codeInp = el('input', { class: 'tliapp-input', type: 'password', placeholder: 'e.g. acme2026', value: ci.code || '' });
          codeInp.style.paddingRight = '46px';
          codeInp.addEventListener('input', function () { ci.code = codeInp.value; });
          var eye = el('button', { class: 'tliapp-eye', type: 'button', 'aria-label': 'Show or hide', text: '👁' });
          eye.addEventListener('click', function () { codeInp.type = (codeInp.type === 'password' ? 'text' : 'password'); eye.classList.toggle('on', codeInp.type === 'text'); });
          card.appendChild(el('label', { class: 'tliapp-field' }, [el('span', { text: 'Company access code' }), el('span', { class: 'tliapp-eyewrap' }, [codeInp, eye])]));

          var chk = el('input', { type: 'checkbox' }); if (ci.optOut) chk.setAttribute('checked', 'checked');
          chk.addEventListener('change', function () { ci.optOut = chk.checked; });
          var disc = el('div', { class: 'tliapp-disclosure' }, [
            el('p', { html: '<b>What it can’t be used for.</b> Your company has agreed in writing not to use this for hiring, promotion, pay, or discipline. It wasn’t built for any of those.' }),
            el('p', { html: '<b>What we keep.</b> We keep the answers, with your name taken off them, to make this better. We’ll publish what we learn, including the parts we don’t like.' }),
            el('label', { class: 'tliapp-optout' }, [chk, el('span', { text: 'Don’t use my answers for that.' })]),
            el('p', { class: 'tliapp-fine2', text: 'You get your full result either way.' })
          ]);
          card.appendChild(disc);
          var btn = el('button', { class: 'tliapp-btn', type: 'button', text: 'Continue' }); if (ci.busy) btn.disabled = true;
          btn.addEventListener('click', function () { if (!ci.first.trim()) { ci.note = 'Please add your first name.'; paint(); return; } submit(true); });
          card.appendChild(btn); card.appendChild(noteEl());
          wrap.appendChild(card);
        }

        function paintDup() {
          var when = ago(ci.dupAt);
          var line = 'Someone at <b>' + esc(ci.company || 'this company') + '</b> already ' + (ci.dupDone ? 'finished as' : 'entered') + ' <b>' + esc(ci.display) + '</b>' + (when ? ', started ' + when : '') + '.';
          var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
            el('div', { class: 'tliapp-eyebrow', text: 'Quick check' }),
            el('h2', { class: 'tliapp-h', text: 'That name is already here' }),
            el('p', { html: line }),
            el('p', { class: 'tliapp-fine', text: ci.dupDone ? 'If that was you, you are all set — check with your organizer. If there are two of you, add your team below.' : 'If that was you a moment ago, pick the first. If there are two of you, pick the second.' })
          ]);
          var b1 = el('button', { class: 'tliapp-btn', type: 'button', text: 'I’m coming back to finish' });
          b1.addEventListener('click', function () { ci.confirmDup = true; ci.suffix = ''; submit(true); });
          var b2 = el('button', { class: 'tliapp-btn tliapp-btn-alt', type: 'button', text: 'I’m a different ' + esc(ci.display) });
          b2.addEventListener('click', function () { ci.stage = 'dupteam'; ci.note = ''; paint(); });
          card.appendChild(b1); card.appendChild(b2);
          wrap.appendChild(card);
        }

        function paintDupTeam() {
          var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
            el('div', { class: 'tliapp-eyebrow', text: 'One quick thing' }),
            el('h2', { class: 'tliapp-h', text: 'What team are you on?' }),
            el('p', { text: 'So your workbook is unmistakably yours. A word or two is enough. It goes on your workbook and nowhere else.' })
          ]);
          var inp = el('input', { class: 'tliapp-input', type: 'text', placeholder: 'e.g. Ops', value: ci.suffix || '' });
          inp.addEventListener('input', function () { ci.suffix = inp.value; });
          card.appendChild(el('label', { class: 'tliapp-field' }, [el('span', { text: 'Your team' }), inp]));
          var btn = el('button', { class: 'tliapp-btn', type: 'button', text: 'Continue' }); if (ci.busy) btn.disabled = true;
          btn.addEventListener('click', function () { if (!ci.suffix.trim()) { ci.note = 'Add a word so we can tell you apart.'; paint(); return; } ci.confirmDup = false; submit(true); });
          card.appendChild(btn); card.appendChild(noteEl());
          wrap.appendChild(card);
        }

        function paintConfirm() {
          var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
            el('div', { class: 'tliapp-eyebrow', text: 'Almost there' }),
            el('h2', { class: 'tliapp-h', text: 'Check your name' }),
            el('p', { text: 'Your workbook will say:' }),
            el('div', { class: 'tliapp-nameplate', text: ci.display }),
            ci.company ? el('p', { class: 'tliapp-fine2', style: 'text-align:center', text: 'for ' + ci.company } ) : null
          ]);
          var yes = el('button', { class: 'tliapp-btn', type: 'button', text: 'Yes, that’s right' }); if (ci.busy) yes.disabled = true;
          yes.addEventListener('click', function () { submit(false); });
          var no = el('button', { class: 'tliapp-btn tliapp-btn-alt', type: 'button', text: 'Change it' });
          no.addEventListener('click', function () { ci.stage = 'form'; ci.confirmDup = false; ci.suffix = ''; ci.note = ''; paint(); });
          card.appendChild(yes); card.appendChild(no); card.appendChild(noteEl());
          wrap.appendChild(card);
        }

        function paint() {
          wrap.innerHTML = '';
          if (ci.stage === 'dup') return paintDup();
          if (ci.stage === 'dupteam') return paintDupTeam();
          if (ci.stage === 'confirm') return paintConfirm();
          return paintForm();
        }
        paint();
      }

      function renderScreen(wrap, s) {
        var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
          s.icon ? el('div', { class: 'tliapp-icon', text: s.icon }) : null,
          s.eyebrow ? el('div', { class: 'tliapp-eyebrow', text: s.eyebrow }) : null,
          el('h2', { class: 'tliapp-h', text: s.h })
        ]);
        s.body.forEach(function (p) { card.appendChild(el('p', { html: p })); });
        var btn = el('button', { class: 'tliapp-btn', type: 'button', text: s.btn });
        btn.addEventListener('click', function () { go(i + 1); });
        card.appendChild(btn);
        wrap.appendChild(card);
      }

      function renderIntake(wrap) {
        var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
          el('div', { class: 'tliapp-eyebrow', text: 'The Leadership Imprint' }),
          el('h2', { class: 'tliapp-h', text: 'Let us start with you' }),
          el('p', { text: cohortId
            ? 'Your organizer set this up for your team. Add your name and the access code they gave you.'
            : 'This report is yours. We put your name on it and nothing else.' })
        ]);
        function field(label, key, ph) {
          var inp = el('input', { class: 'tliapp-input', type: 'text', placeholder: ph || '', value: state.intake[key] || '' });
          inp.addEventListener('input', function () { state.intake[key] = inp.value; });
          return el('label', { class: 'tliapp-field' }, [el('span', { text: label }), inp]);
        }
        var row = el('div', { class: 'tliapp-row' }, [field('First name', 'firstName'), field('Last name', 'lastName')]);
        card.appendChild(row);
        card.appendChild(field('Position or title', 'title', 'e.g. Operations Manager'));
        if (!cohortId) card.appendChild(field('Company or organization', 'company'));

        // Corporate: a company access code field (eye toggle), pre-filled from the ?k= link param.
        var codeInp = null;
        if (cohortId) {
          codeInp = el('input', { class: 'tliapp-input', type: 'password', placeholder: 'e.g. acme2026', value: cohortCode || '' });
          codeInp.style.paddingRight = '46px';
          var eye = el('button', { class: 'tliapp-eye', type: 'button', 'aria-label': 'Show or hide', text: '👁' });
          eye.addEventListener('click', function () { codeInp.type = (codeInp.type === 'password' ? 'text' : 'password'); eye.classList.toggle('on', codeInp.type === 'text'); });
          card.appendChild(el('label', { class: 'tliapp-field' }, [
            el('span', { text: 'Company access code' }),
            el('span', { class: 'tliapp-eyewrap' }, [codeInp, eye])
          ]));
        }

        var btn = el('button', { class: 'tliapp-btn', type: 'button', text: 'Continue' });
        var note = el('div', { class: 'tliapp-note', text: '' });
        btn.addEventListener('click', function () {
          if (!state.intake.firstName.trim()) { note.textContent = cohortId ? 'Please add your first name.' : 'Please add your first name so we can put it on your report.'; return; }
          if (cohortId && !state.cohortSid) {
            btn.disabled = true; note.textContent = 'One moment…';
            var nm = (state.intake.firstName + ' ' + state.intake.lastName).trim();
            var code = codeInp ? codeInp.value.trim() : '';
            fetch(PROXY, { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'same-origin',
              body: JSON.stringify({ action: 'start', c: cohortId, name: nm, code: code }) })
              .then(function (r) { return r.json(); })
              .then(function (d) {
                if (d && d.error === 'badcode') { btn.disabled = false; note.textContent = d.message || 'That company access code is not right. Check with your organizer.'; return; }
                if (d && d.error === 'full') { btn.disabled = false; note.textContent = d.message || 'Every seat for this assessment has been used.'; return; }
                if (d && d.ok && d.sessionId) { state.cohortSid = d.sessionId; if (d.company) state.intake.company = d.company; }
                else { btn.disabled = false; note.textContent = 'Something went wrong. Please try again.'; return; }
                state.startedAt = Date.now(); go(i + 1);
              })
              .catch(function () { btn.disabled = false; note.textContent = 'Could not reach the server. Please try again.'; });
            return;
          }
          go(i + 1);
        });
        card.appendChild(btn); card.appendChild(note);
        wrap.appendChild(card);
      }

      function pick(key, qid, letter) {
        state[key][qid] = letter;
        go(i + 1);
      }

      function renderQ(wrap, step) {
        var q = step.q, key = step.key, fmt = step.fmt;
        var card = el('div', { class: 'tliapp-card tliapp-q' }, [progressBar(step.prog)]);
        if (fmt === 'freq' && q.tf) {
          // True / False validity item (e.g. D4, D5)
          card.appendChild(el('h3', { class: 'tliapp-qtext', text: q.text }));
          var tf = el('div', { class: 'tliapp-opts tliapp-opts--two' });
          [['True', true], ['False', false]].forEach(function (pair) {
            var b = el('button', { class: 'tliapp-opt', type: 'button', text: pair[0] });
            b.addEventListener('click', function () { pick(key, q.id, pair[1]); });
            tf.appendChild(b);
          });
          card.appendChild(tf);
        } else if (fmt === 'freq') {
          card.appendChild(el('h3', { class: 'tliapp-qtext', text: q.text }));
          var scale = el('div', { class: 'tliapp-scale' });
          FREQ.forEach(function (lab, n) {
            var b = el('button', { class: 'tliapp-scalebtn', type: 'button' }, [el('span', { class: 'tliapp-scalenum', text: String(n + 1) }), el('span', { text: lab })]);
            b.addEventListener('click', function () { pick(key, q.id, n + 1); });
            scale.appendChild(b);
          });
          card.appendChild(scale);
        } else if (fmt === 'scenario') {
          card.appendChild(el('h3', { class: 'tliapp-qtext', text: q.prompt }));
          var opts = el('div', { class: 'tliapp-opts' });
          shuffle(['a', 'b', 'c', 'd']).forEach(function (l) {
            var b = el('button', { class: 'tliapp-opt', type: 'button', text: q[l].text });
            b.addEventListener('click', function () { pick(key, q.id, l); });
            opts.appendChild(b);
          });
          card.appendChild(opts);
        } else { // forced choice
          if (q.prompt) card.appendChild(el('h3', { class: 'tliapp-qtext', text: q.prompt }));
          var fo = el('div', { class: 'tliapp-opts tliapp-opts--two' });
          shuffle(['a', 'b']).forEach(function (l) {
            var b = el('button', { class: 'tliapp-opt', type: 'button', text: q[l].text });
            b.addEventListener('click', function () { pick(key, q.id, l); });
            fo.appendChild(b);
          });
          card.appendChild(fo);
        }
        // back link (allowed; no partial save, but within-session back is fine)
        if (i > 0) {
          var back = el('button', { class: 'tliapp-back', type: 'button', text: '‹ Back' });
          back.addEventListener('click', function () { go(i - 1); });
          card.appendChild(back);
        }
        wrap.appendChild(card);
      }

      function renderFleet(wrap) {
        var card = el('div', { class: 'tliapp-card tliapp-screen' }, [
          el('div', { class: 'tliapp-eyebrow', text: 'Your team' }),
          el('h2', { class: 'tliapp-h', text: 'Your team at a glance' }),
          el('p', { text: 'Think about the people who report to you. Roughly what percent of them lead each way? A best guess is fine and it does not need to add up to exactly 100. Leave it blank if you do not manage a team.' })
        ]);
        var defs = [
          { k: 'rocket', t: 'Fast-moving and task-first' }, { k: 'jet', t: 'Fast-moving and people-first' },
          { k: 'tractor', t: 'Measured and task-first' }, { k: 'bus', t: 'Measured and people-first' }
        ];
        var totalEl = el('span', { class: 'tliapp-fleet-total' });
        function refreshTotal() {
          var t = ['rocket', 'jet', 'tractor', 'bus'].reduce(function (s, k) { return s + (Number(state.fleet[k]) || 0); }, 0);
          totalEl.textContent = t ? ('Total: ' + Math.round(t) + '%') : '';
          totalEl.className = 'tliapp-fleet-total' + (t > 100 ? ' is-over' : '');
        }
        defs.forEach(function (d) {
          var inp = el('input', { class: 'tliapp-input tliapp-num', type: 'number', min: '0', max: '100', inputmode: 'numeric', value: state.fleet[d.k] });
          inp.addEventListener('input', function () { state.fleet[d.k] = inp.value; refreshTotal(); });
          card.appendChild(el('label', { class: 'tliapp-field tliapp-field--row' }, [
            el('span', { text: d.t }),
            el('span', { class: 'tliapp-pctwrap' }, [inp, el('span', { class: 'tliapp-pct', text: '%' })])
          ]));
        });
        card.appendChild(totalEl); refreshTotal();
        var btn = el('button', { class: 'tliapp-btn', type: 'button', text: 'Continue' });
        btn.addEventListener('click', function () { go(i + 1); });
        card.appendChild(btn);
        wrap.appendChild(card);
      }

      function buildResponses() {
        var fleet = null, f = state.fleet;
        var total = ['rocket', 'jet', 'tractor', 'bus'].reduce(function (s, k) { return s + (Number(f[k]) || 0); }, 0);
        if (total > 0) fleet = { rocket: Number(f.rocket) || 0, jet: Number(f.jet) || 0, tractor: Number(f.tractor) || 0, bus: Number(f.bus) || 0 };
        return { A: state.A, B: state.B, C: state.C, D: state.D, E: state.E, M: state.M, fleet: fleet };
      }

      function feedbackCard(score, styleVars, label) {
        var sent = false;
        var card = el('div', { class: 'tliapp-fb' }, [el('div', { class: 'tliapp-fb-q', text: label || 'Does this feel like you?' })]);
        var row = el('div', { class: 'tliapp-fb-row' });
        var msg = el('div', { class: 'tliapp-fb-msg', text: '' });
        [['👍', 'Yes', 'yes'], ['👎', 'Not quite', 'no']].forEach(function (o) {
          var b = el('button', { class: 'tliapp-fb-btn', type: 'button', text: o[0] + '  ' + o[1] });
          b.addEventListener('click', function () {
            if (sent) return; sent = true;
            row.querySelectorAll('.tliapp-fb-btn').forEach(function (x) { x.classList.remove('on'); });
            b.classList.add('on');
            msg.textContent = o[2] === 'yes' ? 'Good. That recognition is the point.' : 'That is useful too. A result that does not land is worth sitting with before you decide it is wrong.';
            if (typeof opts.onComplete === 'function') opts.onComplete({ event: 'feedback', value: o[2], style: score.primaryStyle });
          });
          row.appendChild(b);
        });
        card.appendChild(row); card.appendChild(msg);
        return el('div', { class: 'tli-doc', style: styleVars }, [card]);
      }

      // Short "try it" scorer: place the leader from the answered subset only,
      // dividing by how many were answered so a short set is not dampened.
      var SHORT_BLURB = {
        TRACTOR: 'You lead measured and task-first. You are the one who gets the hard, unglamorous work finished and finished right, and a team leans on that more than it ever says out loud.',
        BUS: 'You lead measured and people-first. You keep everyone moving together and no one left behind, which is the quiet reason your team holds under strain.',
        JET: 'You lead fast and people-first. You read the room and move the group quickly, turning energy into momentum before it has a chance to fade.',
        ROCKET: 'You lead fast and task-first. You cut to the decision and drive it through when time is short, which is exactly what a stuck team needs.'
      };
      function shortScore() {
        var An = E.BLOCK_A.slice(0, 5), Bn = E.BLOCK_B.slice(0, 5);
        var fast = 0, af = 0; An.forEach(function (q) { var v = state.A[q.id]; if (v) { af++; if (q[v] && q[v].pole === 'FAST') fast++; } });
        var ppl = 0, bf = 0; Bn.forEach(function (q) { var v = state.B[q.id]; if (v) { bf++; if (q[v] && q[v].pole === 'PEOPLE') ppl++; } });
        var pace = af ? (fast / af) * 100 : 50, priority = bf ? (ppl / bf) * 100 : 50;
        return { pace: pace, priority: priority, style: E.styleFor(pace, priority) };
      }
      function renderShort(wrap) {
        var s = shortScore(), style = s.style, label = E.STYLE_LABEL[style] || style;
        var COL = root.TLI.visuals.COLORS, sc = COL[style] || COL.TRACTOR;
        var styleVars = '--tli-style:' + sc.p + ';--tli-style-dark:' + sc.d + ';--tli-style-light:' + sc.l + ';';
        if (typeof opts.onComplete === 'function') opts.onComplete({ event: 'short', style: style });
        var quad = root.TLI.visuals.grid({ pace: s.pace, priority: s.priority, style: style }, null, { style: style });
        var page = el('div', { class: 'tli-page tliapp-short' }, [
          el('div', { class: 'tli-eyebrow', text: 'Your quick result' }),
          el('h2', { class: 'tli-h', html: 'You lead like a <span style="color:var(--style)">' + label + '</span>' }),
          el('div', { class: 'tliapp-short-grid', html: quad }),
          el('p', { class: 'tliapp-short-blurb', text: SHORT_BLURB[style] })
        ]);
        page.appendChild(feedbackCard(s, styleVars, 'Does “You lead like a ' + label + '” feel like you?'));
        // two paths
        var paths = el('div', { class: 'tliapp-paths' }, [
          el('div', { class: 'tliapp-path' }, [
            el('div', { class: 'tliapp-path-eye', text: 'For you' }),
            el('h4', { text: 'Get your full report' }),
            el('p', { text: 'Your complete profile, how you lead under pressure, your Mirror, your range, and how to lead the other three styles.' }),
            linkBtn(opts.individualUrl || '#', 'Get the full report')
          ]),
          el('div', { class: 'tliapp-path' }, [
            el('div', { class: 'tliapp-path-eye', text: 'For your team' }),
            el('h4', { text: 'Run it across your team' }),
            el('p', { text: 'Every leader on one map: where the group is heavy, which voice is missing, and the friction to expect. This is the Team Imprint.' }),
            linkBtn(opts.teamsUrl || '#', 'Explore the Team Imprint')
          ])
        ]);
        var doc = el('div', { class: 'tli-doc', style: styleVars }, [page, el('div', { class: 'tli-page tliapp-short-cta' }, [
          el('h3', { class: 'tli-h', text: 'Want the full picture?' }), paths
        ])]);
        wrap.appendChild(el('div', { id: 'tli' }, [doc]));
      }
      function linkBtn(href, text) { return el('a', { class: 'tliapp-btn tliapp-pathbtn', href: href, text: text }); }

      function renderReport(wrap) {
        var responses = buildResponses();
        var score = E.score(responses);
        var meta = { firstName: state.intake.firstName, lastName: state.intake.lastName, company: state.intake.company };
        if (cohortId) {
          // Corporate: send the result to the cohort, show a thank-you, never the report.
          fetch(PROXY, { method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'same-origin',
            body: JSON.stringify({ action: 'complete', c: cohortId, sid: state.cohortSid,
              name: (meta.firstName + ' ' + meta.lastName).trim(), style: score.primaryStyle,
              durationMs: Date.now() - (state.startedAt || Date.now()), score: score, responses: responses }) }).catch(function () {});
          if (typeof opts.onComplete === 'function') opts.onComplete({ event: 'complete', cohort: true, score: score, meta: meta });
          wrap.appendChild(el('div', { class: 'tliapp-card tliapp-screen tliapp-thanks' }, [
            el('div', { class: 'tliapp-eyebrow', text: 'All done' }),
            el('h2', { class: 'tliapp-h', text: 'Thank you, ' + (meta.firstName || 'and well done') + '.' }),
            el('p', { html: 'Your Leadership Imprint is complete and <b>on its way to your facilitator</b> for your session.' }),
            el('p', { class: 'tliapp-note', text: 'You will not see your own result here. Your organizer brings it to the session.' })
          ]));
          return;
        }
        if (typeof opts.onComplete === 'function') opts.onComplete({ event: 'complete', score: score, responses: responses, meta: meta });
        var COL = root.TLI.visuals.COLORS, sc = COL[score.primaryStyle] || COL.TRACTOR;
        var styleVars = '--tli-style:' + sc.p + ';--tli-style-dark:' + sc.d + ';--tli-style-light:' + sc.l + ';';
        var host = el('div', { id: 'tli' });

        if (opts.teaser) {
          // Free teaser: the standing-alone summary (style + glance), then an honest unlock.
          host.innerHTML = report.render(score, meta, { tier: 'one' });
          host.appendChild(feedbackCard(score, styleVars, 'Before you unlock the rest – does “You lead like a ' + (root.TLI.engine.STYLE_LABEL[score.primaryStyle] || score.primaryStyle) + '” feel like you?'));
          var up = el('div', { class: 'tliapp-unlock' }, [
            el('h3', { text: 'That is your style. Here is what is behind the lock.' }),
            el('p', { html: 'Your full Leadership Imprint keeps going: your <b>complete profile</b>, how you lead <b>under pressure</b>, your <b>range</b>, how to lead the <b>three styles you are not</b>, what your <b>team is missing</b>, and <b>your Mirror</b> – how you think your team actually sees you.' }),
            (function () { var b = el('button', { class: 'tliapp-btn tliapp-unlock-btn', type: 'button', text: 'Unlock your full Leadership Imprint' }); b.addEventListener('click', function () { if (typeof opts.onUnlock === 'function') opts.onUnlock({ score: score, meta: meta }); }); return b; })()
          ]);
          host.appendChild(el('div', { class: 'tli-doc', style: styleVars }, [el('div', { class: 'tli-page' }, [up])]));
        } else {
          host.innerHTML = report.render(score, meta) + (score.mirror ? report.renderMirror(score, meta) : '');
          host.insertBefore(printToolbar(meta), host.firstChild);
          host.appendChild(feedbackCard(score, styleVars));
        }
        wrap.appendChild(host);
      }

      // Save / print bar shown above the finished report. window.print() lets the
      // browser "Save as PDF" or send to a printer; print CSS isolates #tli.
      function printToolbar(meta) {
        var who = (meta && meta.firstName ? meta.firstName + '’s' : 'your');
        var btn = el('button', { class: 'tliapp-btn tliapp-print', type: 'button', html: '&#8595;&nbsp; Save or print your report' });
        btn.addEventListener('click', function () { window.print(); });
        return el('div', { class: 'tliapp-toolbar' }, [
          el('p', { class: 'tliapp-toolbar-note', text: 'This is ' + who + ' Leadership Imprint. Save it as a PDF or print it – it is yours to keep.' }),
          btn
        ]);
      }

      render();
    }
  };
})(typeof window !== 'undefined' ? window : this);
